import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { crmClientsAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Search,
  Plus,
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  Edit,
  Eye,
  UserCheck,
  Filter,
  Users,
  FileText,
  DollarSign,
  Calendar,
  Loader2,
  ExternalLink,
  X,
  Check,
  AlertCircle
} from "lucide-react";
import { cn } from "../lib/utils";

// Validation helpers matching backend rules
const validateCURP = (curp) => {
  if (!curp) return false;
  const cleaned = curp.toUpperCase().trim();
  if (cleaned.length !== 18) return false;
  const pattern = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d$/;
  return pattern.test(cleaned);
};

const validateRFC = (rfc) => {
  if (!rfc) return false;
  const cleaned = rfc.toUpperCase().trim();
  if (cleaned.length !== 12 && cleaned.length !== 13) return false;
  if (cleaned.length === 12) {
    return /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/.test(cleaned);
  }
  return /^[A-Z&Ñ]{4}\d{6}[A-Z0-9]{3}$/.test(cleaned);
};

const validateIDMEX = (idmex) => {
  if (!idmex) return true; // Optional field
  const cleaned = idmex.replace(/[\s\-]/g, '');
  if (!/^\d+$/.test(cleaned)) return false;
  return cleaned.length >= 9 && cleaned.length <= 13;
};

const statusLabels = {
  prospecto: { label: "Prospecto", color: "bg-slate-100 text-slate-800" },
  activo: { label: "Activo", color: "bg-green-100 text-green-800" },
  recurrente: { label: "Recurrente", color: "bg-blue-100 text-blue-800" },
  vip: { label: "VIP", color: "bg-amber-100 text-amber-800" },
  inactivo: { label: "Inactivo", color: "bg-red-100 text-red-800" }
};

const clientTypeLabels = {
  individual: { label: "Persona Física", icon: User },
  company: { label: "Persona Moral", icon: Building2 }
};

// Sub-component for Client Contracts Tab
function ClientContractsTab({ clientId }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await crmClientsAPI.getContracts(clientId);
        setContracts(response.data || []);
      } catch (error) {
        console.error("Error loading contracts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContracts();
  }, [clientId]);
  
  if (loading) {
    return <div className="flex justify-center p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  
  if (contracts.length === 0) {
    return <div className="text-center text-slate-500 p-4">Sin contratos registrados</div>;
  }
  
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {contracts.map((contract) => (
        <div key={contract.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium text-sm">{contract.folio || contract.id.slice(0, 8)}</p>
            <p className="text-xs text-slate-500">
              {contract.origin} → {contract.destination}
            </p>
            <p className="text-xs text-slate-400">
              {new Date(contract.pickup_datetime).toLocaleDateString("es-MX")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              ${(contract.total_price || 0).toLocaleString("es-MX")}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/contratos/${contract.id}`)}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Sub-component for Client Quotations Tab
function ClientQuotesTab({ clientId }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await crmClientsAPI.getQuotations(clientId);
        setQuotes(response.data || []);
      } catch (error) {
        console.error("Error loading quotations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [clientId]);
  
  if (loading) {
    return <div className="flex justify-center p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  
  if (quotes.length === 0) {
    return <div className="text-center text-slate-500 p-4">Sin cotizaciones registradas</div>;
  }
  
  return (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {quotes.map((quote) => (
        <div key={quote.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium text-sm">{quote.id.slice(0, 8)}</p>
            <p className="text-xs text-slate-500">
              {quote.origin} → {quote.destination}
            </p>
            <p className="text-xs text-slate-400">
              {new Date(quote.created_at).toLocaleDateString("es-MX")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={quote.status === "aceptada" ? "default" : "outline"} className="text-xs">
              {quote.status || "pendiente"}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CRMClientes() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [salesUsers, setSalesUsers] = useState([]);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit | view
  const [selectedClient, setSelectedClient] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formStep, setFormStep] = useState(1); // 1 = select type, 2 = fill data
  const [formData, setFormData] = useState({
    client_type: "",
    name: "",
    legal_name: "",
    address: "",
    phone: "",
    email: "",
    notes: "",
    status: "activo",
    client_owner_id: "",
    curp: "",
    idmex: "",
    rfc: ""
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchClients = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.client_type = typeFilter;
      if (ownerFilter !== "all") params.client_owner_id = ownerFilter;
      
      const response = await crmClientsAPI.getAll(params);
      setClients(response.data);
    } catch (error) {
      console.error("Error loading clients:", error);
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, ownerFilter]);

  const fetchSalesUsers = async () => {
    try {
      const response = await usersAPI.getVendedores();
      setSalesUsers(response.data || []);
    } catch (error) {
      console.error("Error loading sales users:", error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchSalesUsers();
  }, [fetchClients]);

  // Filter clients by search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (client.name || "").toLowerCase().includes(search) ||
      (client.legal_name || "").toLowerCase().includes(search) ||
      (client.phone || "").toLowerCase().includes(search) ||
      (client.email || "").toLowerCase().includes(search) ||
      (client.rfc || "").toLowerCase().includes(search) ||
      (client.curp || "").toLowerCase().includes(search)
    );
  });

  const openCreateModal = () => {
    setFormData({
      client_type: "",
      name: "",
      legal_name: "",
      address: "",
      phone: "",
      email: "",
      notes: "",
      status: "activo",
      client_owner_id: "",
      curp: "",
      idmex: "",
      rfc: ""
    });
    setFormErrors({});
    setFormStep(1);
    setModalMode("create");
    setSelectedClient(null);
    setModalOpen(true);
  };

  const openEditModal = (client) => {
    setFormData({
      client_type: client.client_type || "",
      name: client.name || "",
      legal_name: client.legal_name || "",
      address: client.address || "",
      phone: client.phone || "",
      email: client.email || "",
      notes: client.notes || "",
      status: client.status || "activo",
      client_owner_id: client.client_owner_id || "",
      curp: client.curp || "",
      idmex: client.idmex || "",
      rfc: client.rfc || ""
    });
    setFormErrors({});
    setFormStep(2); // Skip type selection in edit mode
    setModalMode("edit");
    setSelectedClient(client);
    setModalOpen(true);
  };

  const openViewModal = async (client) => {
    try {
      const response = await crmClientsAPI.getById(client.id);
      setSelectedClient(response.data);
      setModalMode("view");
      setModalOpen(true);
    } catch (error) {
      toast.error("Error al cargar detalles del cliente");
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.legal_name.trim()) {
      errors.legal_name = formData.client_type === "individual" 
        ? "Nombre completo es obligatorio" 
        : "Razón social es obligatoria";
    }
    
    // phone is OPTIONAL - removed validation
    
    if (formData.client_type === "individual") {
      if (!formData.curp.trim()) {
        errors.curp = "CURP es obligatorio para Persona Física";
      } else if (!validateCURP(formData.curp)) {
        errors.curp = "CURP inválido. Debe tener 18 caracteres con formato válido";
      }
      
      if (formData.idmex && !validateIDMEX(formData.idmex)) {
        errors.idmex = "IDMEX inválido. Solo números, entre 9 y 13 dígitos";
      }
    }
    
    if (formData.client_type === "company") {
      if (!formData.rfc.trim()) {
        errors.rfc = "RFC es obligatorio para Persona Moral";
      } else if (!validateRFC(formData.rfc)) {
        errors.rfc = "RFC inválido. Debe tener 12 o 13 caracteres con formato válido";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const payload = {
        client_type: formData.client_type,
        name: formData.name || formData.legal_name,
        legal_name: formData.legal_name,
        address: formData.address || null,
        phone: formData.phone,
        email: formData.email || null,
        notes: formData.notes || null,
        status: formData.status,
        client_owner_id: formData.client_owner_id || null
      };
      
      if (formData.client_type === "individual") {
        payload.curp = formData.curp.toUpperCase().trim();
        payload.idmex = formData.idmex ? formData.idmex.trim() : null;
      } else {
        payload.rfc = formData.rfc.toUpperCase().trim();
      }
      
      if (modalMode === "create") {
        await crmClientsAPI.create(payload);
        toast.success("Cliente creado exitosamente");
      } else {
        await crmClientsAPI.update(selectedClient.id, payload);
        toast.success("Cliente actualizado exitosamente");
      }
      
      setModalOpen(false);
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail || "Error al guardar cliente";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const selectClientType = (type) => {
    setFormData(prev => ({ ...prev, client_type: type }));
    setFormStep(2);
  };

  const renderStats = () => {
    const total = clients.length;
    const activos = clients.filter(c => c.status === "activo").length;
    const vip = clients.filter(c => c.status === "vip").length;
    const personas = clients.filter(c => c.client_type === "individual").length;
    const empresas = clients.filter(c => c.client_type === "company").length;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{total}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-600">{activos}</p>
                <p className="text-sm text-slate-500">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-amber-600">{vip}</p>
                <p className="text-sm text-slate-500">VIP</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{personas}</p>
                <p className="text-sm text-slate-500">Personas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-purple-600">{empresas}</p>
                <p className="text-sm text-slate-500">Empresas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Clientes CRM</h1>
            <p className="text-slate-500">Gestiona tu cartera de clientes estructurada</p>
          </div>
          <Button onClick={openCreateModal} data-testid="create-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>

        {/* Stats */}
        {renderStats()}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, teléfono, email, RFC, CURP..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-clients"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40" data-testid="type-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="individual">Persona Física</SelectItem>
                    <SelectItem value="company">Persona Moral</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="status-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="prospecto">Prospecto</SelectItem>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="recurrente">Recurrente</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                    <SelectTrigger className="w-48" data-testid="owner-filter">
                      <SelectValue placeholder="Vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los vendedores</SelectItem>
                      {salesUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-slate-500">
                <Users className="w-12 h-12 mb-4 text-slate-300" />
                <p>No hay clientes</p>
                <Button variant="outline" className="mt-4" onClick={openCreateModal}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primer cliente
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Identificación</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => {
                    const TypeIcon = clientTypeLabels[client.client_type]?.icon || User;
                    return (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-800">
                              {client.name || client.legal_name}
                            </p>
                            {client.name !== client.legal_name && (
                              <p className="text-sm text-slate-500">{client.legal_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">
                              {clientTypeLabels[client.client_type]?.label || client.client_type}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3 text-slate-400" />
                              {client.phone || "-"}
                            </div>
                            {client.email && (
                              <div className="flex items-center gap-1 text-sm text-slate-500">
                                <Mail className="w-3 h-3 text-slate-400" />
                                {client.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {client.client_type === "individual" ? (
                              <>
                                <span className="text-slate-500">CURP:</span> {client.curp || "-"}
                              </>
                            ) : (
                              <>
                                <span className="text-slate-500">RFC:</span> {client.rfc || "-"}
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("text-xs", statusLabels[client.status]?.color)}>
                            {statusLabels[client.status]?.label || client.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {client.client_owner_name || "-"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewModal(client)}
                              data-testid={`view-client-${client.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(client)}
                              data-testid={`edit-client-${client.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen && modalMode !== "view"} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {modalMode === "create" ? "Nuevo Cliente" : "Editar Cliente"}
            </DialogTitle>
          </DialogHeader>

          {formStep === 1 && modalMode === "create" ? (
            // Step 1: Select client type
            <div className="py-6">
              <p className="text-center text-slate-600 mb-6">
                Selecciona el tipo de cliente
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => selectClientType("individual")}
                  className="p-6 border-2 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                  data-testid="select-type-individual"
                >
                  <User className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                  <p className="font-medium">Persona Física</p>
                  <p className="text-sm text-slate-500 mt-1">Cliente individual</p>
                </button>
                <button
                  onClick={() => selectClientType("company")}
                  className="p-6 border-2 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-center"
                  data-testid="select-type-company"
                >
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-purple-500" />
                  <p className="font-medium">Persona Moral</p>
                  <p className="text-sm text-slate-500 mt-1">Empresa u organización</p>
                </button>
              </div>
            </div>
          ) : (
            // Step 2: Form fields
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Type indicator */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                {formData.client_type === "individual" ? (
                  <>
                    <User className="w-5 h-5 text-blue-500" />
                    <span className="font-medium">Persona Física</span>
                  </>
                ) : (
                  <>
                    <Building2 className="w-5 h-5 text-purple-500" />
                    <span className="font-medium">Persona Moral</span>
                  </>
                )}
                {modalMode === "create" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setFormStep(1)}
                  >
                    Cambiar
                  </Button>
                )}
              </div>

              {/* Legal Name / Razón Social */}
              <div>
                <Label>
                  {formData.client_type === "individual" ? "Nombre Completo *" : "Razón Social *"}
                </Label>
                <Input
                  value={formData.legal_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                  placeholder={formData.client_type === "individual" ? "Juan Pérez García" : "Empresa S.A. de C.V."}
                  className={formErrors.legal_name ? "border-red-500" : ""}
                  data-testid="input-legal-name"
                />
                {formErrors.legal_name && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.legal_name}</p>
                )}
              </div>

              {/* Display Name (optional) */}
              <div>
                <Label>Nombre de Display (opcional)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre corto para mostrar"
                  data-testid="input-name"
                />
              </div>

              {/* Address */}
              <div>
                <Label>Domicilio</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Calle, número, colonia, ciudad"
                  data-testid="input-address"
                />
              </div>

              {/* Type-specific fields */}
              {formData.client_type === "individual" ? (
                <>
                  {/* CURP */}
                  <div>
                    <Label>CURP *</Label>
                    <Input
                      value={formData.curp}
                      onChange={(e) => setFormData(prev => ({ ...prev, curp: e.target.value.toUpperCase() }))}
                      placeholder="XXXX000000XXXXXX00"
                      maxLength={18}
                      className={formErrors.curp ? "border-red-500" : ""}
                      data-testid="input-curp"
                    />
                    <p className="text-xs text-slate-500 mt-1">18 caracteres exactos</p>
                    {formErrors.curp && (
                      <p className="text-sm text-red-500 mt-1">{formErrors.curp}</p>
                    )}
                  </div>

                  {/* IDMEX */}
                  <div>
                    <Label>IDMEX (Clave de Elector)</Label>
                    <Input
                      value={formData.idmex}
                      onChange={(e) => setFormData(prev => ({ ...prev, idmex: e.target.value.replace(/\D/g, '') }))}
                      placeholder="123456789"
                      maxLength={13}
                      className={formErrors.idmex ? "border-red-500" : ""}
                      data-testid="input-idmex"
                    />
                    <p className="text-xs text-slate-500 mt-1">Solo números, 9 a 13 dígitos (opcional)</p>
                    {formErrors.idmex && (
                      <p className="text-sm text-red-500 mt-1">{formErrors.idmex}</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* RFC */}
                  <div>
                    <Label>RFC *</Label>
                    <Input
                      value={formData.rfc}
                      onChange={(e) => setFormData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                      placeholder="XXX000000XXX"
                      maxLength={13}
                      className={formErrors.rfc ? "border-red-500" : ""}
                      data-testid="input-rfc"
                    />
                    <p className="text-xs text-slate-500 mt-1">12 o 13 caracteres</p>
                    {formErrors.rfc && (
                      <p className="text-sm text-red-500 mt-1">{formErrors.rfc}</p>
                    )}
                  </div>
                </>
              )}

              {/* Contact info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="33 1234 5678"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="cliente@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospecto">Prospecto</SelectItem>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="recurrente">Recurrente</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Client Owner (Admin only) */}
              {isAdmin && (
                <div>
                  <Label>Vendedor Asignado (opcional)</Label>
                  <Select
                    value={formData.client_owner_id || "none"}
                    onValueChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      client_owner_id: value === "none" ? "" : value 
                    }))}
                  >
                    <SelectTrigger data-testid="select-owner">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {salesUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas adicionales sobre el cliente..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
            </div>
          )}

          {formStep === 2 && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {modalMode === "create" ? "Crear Cliente" : "Guardar Cambios"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={modalOpen && modalMode === "view"} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedClient?.client_type === "individual" ? (
                <User className="w-5 h-5 text-blue-500" />
              ) : (
                <Building2 className="w-5 h-5 text-purple-500" />
              )}
              {selectedClient?.name || selectedClient?.legal_name}
            </DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-6 py-4">
              {/* Client Stats */}
              {selectedClient.stats && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <FileText className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                    <p className="text-lg font-bold">{selectedClient.stats.contracts_count}</p>
                    <p className="text-xs text-slate-500">Contratos</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-500" />
                    <p className="text-lg font-bold">
                      ${(selectedClient.stats.total_revenue || 0).toLocaleString("es-MX")}
                    </p>
                    <p className="text-xs text-slate-500">Ingresos</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <Calendar className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                    <p className="text-sm font-medium">
                      {selectedClient.stats.last_service_date 
                        ? new Date(selectedClient.stats.last_service_date).toLocaleDateString("es-MX")
                        : "-"
                      }
                    </p>
                    <p className="text-xs text-slate-500">Último servicio</p>
                  </div>
                </div>
              )}

              {/* Tabs for client info, contracts, quotes */}
              <Tabs defaultValue="info" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Información</TabsTrigger>
                  <TabsTrigger value="contracts">Contratos</TabsTrigger>
                  <TabsTrigger value="quotes">Cotizaciones</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="mt-4">
                  {/* Client Info */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <User className="w-4 h-4 mt-1 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">
                          {selectedClient.client_type === "individual" ? "Nombre completo" : "Razón social"}
                        </p>
                        <p className="font-medium">{selectedClient.legal_name}</p>
                      </div>
                    </div>

                    {selectedClient.address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 mt-1 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Domicilio</p>
                          <p>{selectedClient.address}</p>
                        </div>
                      </div>
                    )}

                    {selectedClient.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 mt-1 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Teléfono</p>
                          <p>{selectedClient.phone}</p>
                        </div>
                      </div>
                    )}

                    {selectedClient.email && (
                      <div className="flex items-start gap-3">
                        <Mail className="w-4 h-4 mt-1 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Email</p>
                          <p>{selectedClient.email}</p>
                        </div>
                      </div>
                    )}

                    {/* Identification */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      {selectedClient.client_type === "individual" ? (
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm text-slate-500">CURP: </span>
                            <span className="font-mono">{selectedClient.curp || "-"}</span>
                          </div>
                          {selectedClient.idmex && (
                            <div>
                              <span className="text-sm text-slate-500">IDMEX: </span>
                              <span className="font-mono">{selectedClient.idmex}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="text-sm text-slate-500">RFC: </span>
                          <span className="font-mono">{selectedClient.rfc || "-"}</span>
                        </div>
                      )}
                    </div>

                    {/* Owner */}
                    {selectedClient.client_owner_name && (
                      <div className="flex items-start gap-3">
                        <UserCheck className="w-4 h-4 mt-1 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Vendedor asignado</p>
                          <p>{selectedClient.client_owner_name}</p>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {selectedClient.notes && (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm text-amber-800">{selectedClient.notes}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="contracts" className="mt-4">
                  <ClientContractsTab clientId={selectedClient.id} />
                </TabsContent>
                
                <TabsContent value="quotes" className="mt-4">
                  <ClientQuotesTab clientId={selectedClient.id} />
                </TabsContent>
              </Tabs>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={() => {
              setModalMode("edit");
              openEditModal(selectedClient);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
